import React from "react";
import { Navigate, useParams } from "react-router-dom";
import { rememberReferralCode } from "../services/referralCode";

export interface ReferralLinkPageProps {
  /**
   * The screen the visitor lands on once the code is parked.
   *
   * A prop rather than read off the path, because the two referral paths want
   * different answers and neither should have to be inferred. New links are
   * `/signup/<code>` and land on the sign-up form — somebody following a
   * partner's invitation has no account yet. Links already in the world are
   * `/login/<code>` and still land on the login screen, which is the screen
   * that URL names.
   */
  to: string;
}

/**
 * Where a partner's referral link lands: `/signup/<referral_code>`, or
 * `/login/<referral_code>` for a link issued before the generator changed.
 *
 * The link is the server's design - it is stored on the partner and printed
 * in their agreement - so this app has to answer that URL whatever it would
 * have chosen itself. What it does with it is this app's decision: park the
 * code and get it out of the address bar.
 *
 * Nothing is rendered. The visitor sees the ordinary screen named by the path
 * they followed, because that is what they were sent to; a partner's link is a
 * way in, not a different way in. Whether they sign up or log in makes no
 * difference to the code, which is why it is parked in storage rather than
 * carried through the URLs of a flow that can fork, leave for Google and come
 * back as a fresh document. Only `POST /onboarding/workspace/` can spend it,
 * and that is several screens away.
 *
 * ## Why it redirects rather than rendering that screen here
 *
 * Replacing the URL with `/signup` drops the partner's code from the address
 * bar, from the back button, and from the `Referer` on every request the
 * screens after it make. The header of `paths.ts` sets that rule out for this
 * app's own URLs; a code naming a commercial relationship between Naurist and
 * a third party is exactly the kind of thing it is there for.
 *
 * It also keeps one sign-up screen rather than two. The guard on `/signup`
 * decides what a signed-in visitor sees, and this path gets that same answer
 * for free instead of a second copy of the rule that could drift from it.
 *
 * ## Signed in already
 *
 * The code is still parked, and deliberately so. A signed-in visitor is
 * redirected on by the guard and will usually have a workspace, where the
 * parked code is read and goes nowhere: the backend sets a referral when the
 * practice is created and never afterwards. But a session that stopped short
 * of onboarding - signed up, closed the tab, came back through a partner's
 * link - still has that call ahead of it, and that one should carry the code.
 * Judging which case this is from out here would mean guessing; parking it
 * costs nothing and lets the one call that knows decide.
 */
const ReferralLinkPage: React.FC<ReferralLinkPageProps> = ({ to }) => {
  const { referralCode } = useParams<{ referralCode: string }>();

  // During render on purpose. The write is idempotent - the same code stored
  // twice is the same storage - so StrictMode's double render in development
  // is harmless, and this is the only point at which the code is certain to
  // have been read before `<Navigate>` takes the URL away. In an effect it
  // would be racing the redirect's own effect for no gain.
  rememberReferralCode(referralCode);

  return <Navigate to={to} replace />;
};

export default ReferralLinkPage;
